import { Link } from '@tanstack/react-router';
import { CheckCircle2, Play, Lock, ChevronLeft, ChevronRight } from 'lucide-react';

import type { ModuleWithProgress, TopicWithStatus } from '@/lib/api/lms';

const UPNEXT_CAP = 3;

interface NumberedTopic { topic: TopicWithStatus; n: number }

function group(topics: NumberedTopic[], currentTopicId: string) {
  const completed = topics.filter((t) => t.topic.status === 'completed' && t.topic.id !== currentTopicId);
  const currentIdx = topics.findIndex((t) => t.topic.id === currentTopicId);
  const current = currentIdx >= 0 ? topics[currentIdx] : null;
  // Everything after the current topic that isn't already completed.
  const future = topics.filter((t, i) =>
    t.topic.id !== currentTopicId && t.topic.status !== 'completed' && (currentIdx < 0 || i > currentIdx));
  return {
    completed,
    current,
    upNext: future.slice(0, UPNEXT_CAP),
    upcoming: future.slice(UPNEXT_CAP),
  };
}

function Section({ label, count, children }: { label: string; count?: number; children: React.ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <p className="px-1 text-[11px] font-bold uppercase tracking-wide text-[#9ca3af]">
        {label}{count != null ? ` (${count})` : ''}
      </p>
      <ul className="mt-1.5 space-y-0.5">{children}</ul>
    </div>
  );
}

function Row({ item, batchId, kind, current }: {
  item: NumberedTopic; batchId: string; kind: 'completed' | 'current' | 'future'; current?: boolean;
}) {
  const icon = kind === 'completed'
    ? <CheckCircle2 className="size-4 text-[#16a34a]" />
    : kind === 'current'
      ? <Play className="size-4 translate-x-px fill-[#8b5cf6] text-[#8b5cf6]" />
      : <Lock className="size-4 text-[#d1d5db]" />;
  return (
    <li>
      <Link
        to="/courses/$batchId/topics/$topicId"
        params={{ batchId, topicId: item.topic.id }}
        aria-current={current ? 'page' : undefined}
        className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
          current
            ? 'bg-[#f5f3ff] font-semibold text-[#8b5cf6]'
            : kind === 'completed'
              ? 'text-[#374151] hover:bg-[#f9fafb]'
              : 'text-[#6b7280] hover:bg-[#f9fafb]'
        }`}
      >
        <span className="flex-shrink-0">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{item.n}. {item.topic.title}</span>
      </Link>
    </li>
  );
}

export function LessonRail({
  module, batchId, currentTopicId, onCollapse,
}: {
  module: ModuleWithProgress | null;
  batchId: string;
  currentTopicId: string;
  onCollapse?: () => void;
}) {
  if (!module) return null;
  const sorted = [...module.topics].sort((a, b) => a.sort_order - b.sort_order);
  const numbered: NumberedTopic[] = sorted.map((topic, i) => ({ topic, n: i + 1 }));
  const { completed, current, upNext, upcoming } = group(numbered, currentTopicId);

  return (
    <aside className="rounded-2xl border border-[#ececec] bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="font-bold text-[#111827]">Topic Navigation</p>
        {onCollapse && (
          <button type="button" onClick={onCollapse} aria-label="Collapse navigation"
            className="rounded-md p-1 text-[#9ca3af] transition-colors hover:bg-[#f3f4f6] hover:text-[#6b7280]">
            <ChevronLeft className="size-4" />
          </button>
        )}
      </div>

      <div className="mt-3">
        {completed.length > 0 && (
          <Section label="Completed" count={completed.length}>
            {completed.map((it) => <Row key={it.topic.id} item={it} batchId={batchId} kind="completed" />)}
          </Section>
        )}
        {current && (
          <Section label="Current Topic">
            <Row item={current} batchId={batchId} kind="current" current />
          </Section>
        )}
        {upNext.length > 0 && (
          <Section label="Next Up" count={upNext.length}>
            {upNext.map((it) => <Row key={it.topic.id} item={it} batchId={batchId} kind="future" />)}
          </Section>
        )}
        {upcoming.length > 0 && (
          <Section label="Upcoming" count={upcoming.length}>
            {upcoming.map((it) => <Row key={it.topic.id} item={it} batchId={batchId} kind="future" />)}
          </Section>
        )}
      </div>

      <Link to="/courses/$batchId/learn" params={{ batchId }}
        className="mt-3 inline-flex items-center gap-1 px-1 text-sm font-semibold text-[#8b5cf6] transition-opacity hover:opacity-80">
        View all {module.topic_count} topics<ChevronRight className="size-3.5" />
      </Link>

      {onCollapse && (
        <button type="button" onClick={onCollapse}
          className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#e5e7eb] py-2.5 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f9fafb]">
          <ChevronLeft className="size-4" />Collapse
        </button>
      )}
    </aside>
  );
}
